import { useCallback, useMemo, useState } from "react";

import { isEmpty } from "lodash";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Combobox } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { ComboboxWithSelectAll } from "app/pages/sales-intelligence/common-components/dropdown/ComboboxWithSelectAll";
import {
    ProfileAccessLevel,
    type ProfileDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";

import useMultiselectHandler from "../../../hooks/useMultiselectHandler";
import useSignalFilterDropdownLabel from "../../../hooks/useSignalFilterDropdownLabel";
import { FilterType } from "../../../models/services/FilterService";
import filterService from "../../../services/filters/filterService";
import FilterButtonLabel from "../../filters/shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "../shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import { DROPDOWN_DEFAULT_WIDTH } from "../shared/FilterMultiselectDropdown/const";
import SignalFilterDropdownLabel from "../shared/SignalFilterDropdownLabel/SignalFilterDropdownLabel";
import { StyledSignalFilterBlock } from "../shared/styles";

type FilterValue = ReadonlyArray<ProfileDto["id"]> | undefined;

type PersonaItem = {
    id: ProfileDto["id"];
    text: string;
    profile: ProfileDto;
};

export type SignalPersonasFilterProps = {
    filterKey: string;
    selectedValue: FilterValue;
    options: ReadonlyArray<ProfileDto>;
    onValueChange(filterValue: FilterValue): void;
};

const SignalPersonasFilter = ({ filterKey, selectedValue, options, onValueChange }: SignalPersonasFilterProps) => {
    const { translate: translateI18n } = useTranslate();
    const comparableSelectedValue = useMemo(() => selectedValue ?? [], [selectedValue]);
    // Empty is the stored "any persona" value, which the UI treats as full selection. Track an
    // explicit uncheck of "Any" so deselect-all can show none selected without losing that meaning.
    const [isAnyUnchecked, setIsAnyUnchecked] = useState(false);
    const isEmptySelection = isEmpty(comparableSelectedValue);
    const isFullSelection =
        !isAnyUnchecked && (isEmptySelection || options.length === comparableSelectedValue.length);
    const isPartiallySelected = !isEmptySelection && !isFullSelection;

    const items = useMemo(
        (): readonly PersonaItem[] =>
            options.map((profile) => ({
                id: profile.id,
                text: profile.name,
                profile,
            })),
        [options],
    );

    const buildFilterName = useCallback(() => {
        const nameBuilder = filterService.getFilterName(FilterType.EmployeeProfileIds);

        return nameBuilder.buildName({ employeeProfileIds: comparableSelectedValue }, options);
    }, [comparableSelectedValue, options]);

    const { model: filterLabelModel } = useSignalFilterDropdownLabel({ buildName: buildFilterName });

    const handleSelectionChange = useCallback(
        (values: ReadonlyArray<ProfileDto["id"]>) => {
            const isEveryProfileSelected = options.length > 0 && values.length === options.length;

            onValueChange(isEveryProfileSelected ? [] : values);
        },
        [onValueChange, options.length],
    );

    const { onSelect } = useMultiselectHandler({
        selectedValue,
        onValueChange: handleSelectionChange,
    });

    const isItemSelected = useCallback(
        (item: PersonaItem) => !isAnyUnchecked && (isFullSelection || comparableSelectedValue.includes(item.id)),
        [comparableSelectedValue, isAnyUnchecked, isFullSelection],
    );

    const handleSelectAll = useCallback(() => {
        setIsAnyUnchecked(isFullSelection);
        onValueChange([]);
    }, [isFullSelection, onValueChange]);

    const handleSelect = useCallback(
        (item: PersonaItem) => {
            const profileId = item.id;
            const isSelected = isItemSelected(item);

            if (isAnyUnchecked) {
                setIsAnyUnchecked(false);
                onValueChange([profileId]);
                return;
            }

            if (isFullSelection && isEmpty(comparableSelectedValue)) {
                onValueChange(options.filter(({ id }) => id !== profileId).map(({ id }) => id));
            } else {
                onSelect(profileId, isSelected);
            }
        },
        [comparableSelectedValue, isAnyUnchecked, isFullSelection, isItemSelected, onSelect, onValueChange, options],
    );

    const renderButton = ({ isOpen, disabled }: { isOpen: boolean; disabled?: boolean }) => (
        <SignalFilterDropdownButton
            id={filterKey}
            isOpen={isOpen}
            disabled={disabled}
            hasValue
        >
            <FilterButtonLabel>
                <SignalFilterDropdownLabel model={filterLabelModel} />
            </FilterButtonLabel>
        </SignalFilterDropdownButton>
    );

    const renderOption = (item: PersonaItem, isSelected: boolean) => {
        const { profile } = item;
        const isSharedProfile = profile.accessLevel === ProfileAccessLevel.Account;

        return (
            <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="8px"
                width="100%"
            >
                <Flex
                    alignItems="center"
                    gap="8px"
                    flex="1"
                    minWidth={0}
                >
                    <Combobox.OptionLabel>
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon["500"]}
                        >
                            {profile.name}
                        </Text>
                    </Combobox.OptionLabel>
                    <Badge
                        bgColor={isSharedProfile ? colorsPalettes.carbon["25"] : colorsPalettes.indigo["100"]}
                        color={isSharedProfile ? colorsPalettes.carbon["400"] : colorsPalettes.indigo["400"]}
                        type="custom"
                    >
                        {translateI18n(isSharedProfile ? "si.playbooks.type.shared" : "si.playbooks.type.private")}
                    </Badge>
                </Flex>
                <Combobox.OptionCheckbox isChecked={isSelected} />
            </Flex>
        );
    };

    return (
        <StyledSignalFilterBlock id={filterKey}>
            <ComboboxWithSelectAll
                items={items}
                isFullSelection={isFullSelection}
                isPartiallySelected={isPartiallySelected}
                isItemSelected={isItemSelected}
                selectAllLabel={translate(`si.signals.filters.${filterKey}.name.any`)}
                hasSearch
                isTopLayer
                width={DROPDOWN_DEFAULT_WIDTH}
                searchPlaceHolder={translate(`si.signals.filters.${filterKey}.search.placeholder`)}
                dataAutomation={`signal-filter-combobox-${filterKey}`}
                onSelectAll={handleSelectAll}
                onSelect={handleSelect}
                renderButton={renderButton}
                renderOption={renderOption}
            />
        </StyledSignalFilterBlock>
    );
};

export default SignalPersonasFilter;
